<template>
	<div
		id="business-contact-form"
		class="py-12 sm:py-16 max-w-container mx-auto px-4"
	>
		<div class="w-full mx-auto flex justify-center items-center flex-col leading-tight pb-12 sm:pb-16">
			<h2 class="w-full text-center text-4xl lg:text-5xl font-semibold text-primary text-pretty">
				{{ $t('business.form.title1') }} <br>
				<span class="w-full text-center text-4xl font-medium text-default text-pretty">
					{{ $t('business.form.title2') }}
				</span>
			</h2>
		</div>
		<UForm
			:id="BUSINESS_CONTACT_FORM_ID"
			class="w-full"
			:state="formState"
			:schema="schema"
			@error="onError"
			@submit.prevent="handleFormSubmit"
		>
			<fieldset class="grid grid-cols-1 md:gap-x-12 gap-y-4 md:grid-cols-2">
				<legend class="pb-4 text-lg font-semibold">
					{{ $t('business.form.business-information') }}
				</legend>
				<UFormField
					name="businessName"
					:label="$t('business.form.business-name.label')"
					required
				>
					<UInput
						:id="BUSINESS_CONTACT_FORM_FIELD_IDS.name"
						v-model="formState.businessName"
						:label="$t('business.form.business-name.label')"
						:placeholder="$t('business.form.business-name.label')"
						:autocomplete="ORGANIZATION_AUTOCOMPLETE"
						class="w-full"
						@focus="trackFormEvent($event, trackingLocation)"
						@blur="trackFormEvent($event, trackingLocation)"
						@input="trackFormEvent($event, trackingLocation)"
						@mouseenter="trackFormEvent($event, trackingLocation)"
					/>
				</UFormField>
				<UFormField
					name="businessOwner"
					:label="$t('business.form.business-owner.label')"
					required
				>
					<UInput
						:id="BUSINESS_CONTACT_FORM_FIELD_IDS.owner"
						v-model="formState.businessOwner"
						:label="$t('business.form.business-owner.label')"
						:placeholder="$t('business.form.business-owner.label')"
						:autocomplete="NAME_AUTOCOMPLETE"
						class="w-full"
						@focus="trackFormEvent($event, trackingLocation)"
						@blur="trackFormEvent($event, trackingLocation)"
						@input="trackFormEvent($event, trackingLocation)"
						@mouseenter="trackFormEvent($event, trackingLocation)"
					/>
				</UFormField>
				<UFormField
					name="businessAddress"
					:label="$t('business.form.business-address.label')"
					required
				>
					<UInput
						:id="BUSINESS_CONTACT_FORM_FIELD_IDS.address"
						v-model="formState.businessAddress"
						:label="$t('business.form.business-address.label')"
						:placeholder="$t('business.form.business-address.label')"
						:autocomplete="STREET_ADDRESS_AUTOCOMPLETE"
						class="w-full"
						@focus="trackFormEvent($event, trackingLocation)"
						@blur="trackFormEvent($event, trackingLocation)"
						@input="trackFormEvent($event, trackingLocation)"
						@mouseenter="trackFormEvent($event, trackingLocation)"
					/>
				</UFormField>
				<UFormField
					name="businessCity"
					:label="$t('business.form.business-city.label')"
					required
				>
					<UInput
						:id="BUSINESS_CONTACT_FORM_FIELD_IDS.city"
						v-model="formState.businessCity"
						:label="$t('business.form.business-city.label')"
						:placeholder="$t('business.form.business-city.label')"
						:autocomplete="CITY_AUTOCOMPLETE"
						class="w-full"
						@focus="trackFormEvent($event, trackingLocation)"
						@blur="trackFormEvent($event, trackingLocation)"
						@input="trackFormEvent($event, trackingLocation)"
						@mouseenter="trackFormEvent($event, trackingLocation)"
					/>
				</UFormField>
				<UFormField
					name="businessState"
					:label="$t('business.form.business-state.label')"
					required
				>
					<USelect
						:id="BUSINESS_CONTACT_FORM_FIELD_IDS.state"
						v-model="formState.businessState"
						:items="availableStates"
						:label="$t('business.form.business-state.label')"
						:placeholder="$t('business.form.business-state.placeholder')"
						class="w-full h-12"
						size="md"
						@focus="loadStates"
						@focusin="trackFormEvent($event, trackingLocation)"
						@focusout="trackFormEvent($event, trackingLocation)"
						@change="trackFormEvent($event, trackingLocation)"
						@mouseenter="trackFormEvent($event, trackingLocation)"
					/>
				</UFormField>
				<UFormField
					name="businessZip"
					:label="$t('business.form.business-zip.label')"
					required
				>
					<UInput
						:id="BUSINESS_CONTACT_FORM_FIELD_IDS.zip"
						v-model="formState.businessZip"
						:label="$t('business.form.business-zip.label')"
						:placeholder="$t('business.form.business-zip.label')"
						:autocomplete="ZIP_CODE_AUTOCOMPLETE"
						class="w-full"
						@focus="trackFormEvent($event, trackingLocation)"
						@blur="trackFormEvent($event, trackingLocation)"
						@input="trackFormEvent($event, trackingLocation)"
						@mouseenter="trackFormEvent($event, trackingLocation)"
					/>
				</UFormField>
			</fieldset>
			<fieldset class="grid grid-cols-1 md:gap-x-12 gap-y-2 md:grid-cols-2">
				<legend class="pt-8 pb-4 text-lg font-semibold">
					{{ $t('business.form.contact-information') }}
				</legend>
				<UFormField
					name="businessPhone"
					:label="$t('business.form.business-phone.label')"
					required
				>
					<FormPhoneInput
						:id="BUSINESS_CONTACT_FORM_FIELD_IDS.phone"
						v-model="formState.businessPhone"
						@update:model-value="formState.businessPhone = $event"
						@focus="trackFormEvent($event, trackingLocation)"
						@blur="trackFormEvent($event, trackingLocation)"
						@input="trackFormEvent($event, trackingLocation)"
						@mouseenter="trackFormEvent($event, trackingLocation)"
					/>
				</UFormField>
				<UFormField
					name="businessEmail"
					:label="$t('business.form.business-email.label')"
					required
				>
					<UInput
						:id="BUSINESS_CONTACT_FORM_FIELD_IDS.email"
						v-model="formState.businessEmail"
						:label="$t('business.form.business-email.label')"
						:placeholder="$t('business.form.business-email.label')"
						:autocomplete="EMAIL_AUTOCOMPLETE"
						class="w-full"
						@focus="trackFormEvent($event, trackingLocation)"
						@blur="trackFormEvent($event, trackingLocation)"
						@input="trackFormEvent($event, trackingLocation)"
						@mouseenter="trackFormEvent($event, trackingLocation)"
					/>
				</UFormField>
			</fieldset>
			<div class="flex justify-center py-4">
				<UButton
					:id="BUSINESS_CONTACT_FORM_FIELD_IDS.submit"
					type="submit"
					:label="$t('business.form.submit')"
					size="xl"
					auto-loading
					class="w-full max-w-sm flex justify-center text-center mt-8 lg:mt-12"
					:ui="{
						label: 'uppercase font-bold'
					}"
					@click="trackFormEvent($event, trackingLocation)"
					@mouseenter="trackFormEvent($event, trackingLocation)"
					@focus="trackFormEvent($event, trackingLocation)"
				/>
			</div>
		</UForm>
	</div>
</template>

<script setup lang="ts">
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { useZod } from '@/composables/useZod'
import {
	CITY_AUTOCOMPLETE,
	EMAIL_AUTOCOMPLETE,
	NAME_AUTOCOMPLETE,
	ORGANIZATION_AUTOCOMPLETE,
	STREET_ADDRESS_AUTOCOMPLETE,
	ZIP_CODE_AUTOCOMPLETE
} from '@/constants/form/autocompletes.js'
import {
	BUSINESS_CONTACT_FORM_FIELD_IDS,
	BUSINESS_CONTACT_FORM_ID,
	BUSINESS_CONTACT_FORM_TYPE
} from '@/constants/ga4/tracking-ids/forms/business-contact'
import {
	API_BASE,
	API_ENDPOINT_BUSINESS_CONTACT
} from '@/constants/index.js'
import { trimPhone } from '@/utils/formatPhone'
import type { FormErrorEvent } from '@nuxt/ui'
import { useI18n } from 'vue-i18n'

const props = defineProps({
	trackingLocation: {
		type: String,
		required: true
	}
})

const { trackingLocation } = toRefs(props)

const { bugsnagReport } = useErrorReporter()
const toast = useToast()
const { t } = useI18n({
	useScope: 'global'
})

const {
	trackFormSubmit,
	trackError,
	trackFormEvent
} = useGA4Tracking()

// Get form formState and schema from Zod composable
const {
	businessFormState: formState,
	businessFormSchema: schema
} = useZod()

// Lazy load states
const statesLoaded = ref(false)
const availableStates = ref<Array<{
	value: string
	label: string
}>>([])

const loadStates = async () => {
	if (!statesLoaded.value) {
		const { US_STATES } = await import('@/constants/form/us-states')
		availableStates.value = US_STATES
		statesLoaded.value = true
	}
}

// Form data for submission
const formData = computed(() => {
	return {
		name: formState.businessName?.trim(),
		owner: formState.businessOwner?.trim(),
		address: formState.businessAddress?.trim(),
		city: formState.businessCity?.trim(),
		formState: formState.businessState,
		zip: formState.businessZip?.trim(),
		phoneNumber: trimPhone(formState.businessPhone || ''),
		email: formState.businessEmail?.trim()
	}
})

// Reset form
const resetForm = () => {
	formState.businessName = ''
	formState.businessOwner = ''
	formState.businessAddress = ''
	formState.businessCity = ''
	formState.businessState = ''
	formState.businessZip = ''
	formState.businessPhone = ''
	formState.businessEmail = ''
}

// Handle form validation errors
async function onError(event: FormErrorEvent) {
	if (event?.errors?.[0]?.id) {
		const element = document.getElementById(event.errors[0].id)
		if (element) {
			element?.focus()
			element?.scrollIntoView({
				behavior: 'smooth',
				block: 'center'
			})
		}
		trackError('Business Form Validation Error: ' + event.errors[0].message, BUSINESS_CONTACT_FORM_ID)
	}
}

// Handle form submission
const handleFormSubmit = async () => {
	// NOTE: Don't send sensitive data like name/phone/email/street address
	trackFormSubmit(BUSINESS_CONTACT_FORM_ID, BUSINESS_CONTACT_FORM_TYPE, trackingLocation.value, {
		city: formState.businessCity || '',
		formState: formState.businessState || '',
		zip: formState.businessZip || ''
	})

	await $fetch(`${API_BASE}${API_ENDPOINT_BUSINESS_CONTACT}`, {
		method: 'POST',
		body: formData.value,
		onRequestError({ error }) {
			const errorMessage = error?.message || 'Error submitting contact form'
			bugsnagReport({
				error: new Error(errorMessage),
				showToast: true,
				toast: {
					title: t('business.form.error-toast.title'),
					message: t('business.form.error-toast.message')
				}
			})
		},
		onResponse({ response }) {
			if (!response?.ok) {
				throw new Error(response._data?.error || response._data?.errors?.[0]?.title || 'Error submitting form')
			}
			if (response.status === 200) {
				resetForm()
				toast.add({
					title: t('business.form.success-toast.title'),
					description: t('business.form.success-toast.message'),
					color: 'success'
				})
			}
		}
	}).catch(error => {
		const errorMessage = error?.message || t('business.form.error-toast.message')
		bugsnagReport({
			error: new Error(errorMessage),
			showToast: true,
			toast: {
				title: t('business.form.error-toast.title'),
				message: errorMessage
			}
		})
	})
}
</script>
